import React from 'react';

interface ScopeComparisonCardsProps {
  data: {
    baseYear: {
      scope1: number;
      scope2: number;
      scope3: number;
      total: number;
    };
    selectedYear: {
      scope1: number;
      scope2: number;
      scope3: number;
      total: number;
    };
    baseYearValue: number;
    selectedYearValue: number;
  } | undefined;
}

export function ScopeComparisonCards({ data }: ScopeComparisonCardsProps) {
  if (!data) return null;

  const scopes = [
    {
      name: 'Scope 1',
      baseValue: data.baseYear.scope1,
      reportingValue: data.selectedYear.scope1,
      color: 'border-red-200 bg-red-50',
      textColor: 'text-red-700',
      icon: '🔥'
    },
    {
      name: 'Scope 2',
      baseValue: data.baseYear.scope2,
      reportingValue: data.selectedYear.scope2,
      color: 'border-green-200 bg-green-50',
      textColor: 'text-green-700',
      icon: '⚡'
    },
    {
      name: 'Scope 3',
      baseValue: data.baseYear.scope3,
      reportingValue: data.selectedYear.scope3,
      color: 'border-blue-200 bg-blue-50',
      textColor: 'text-blue-700',
      icon: '🏭'
    }
  ];

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {scopes.map((scope) => {
          const change = scope.reportingValue - scope.baseValue;
          const changePercent = scope.baseValue > 0 ? (change / scope.baseValue) * 100 : 0;
          const isIncrease = change >= 0;

          return (
            <div
              key={scope.name}
              className={`rounded-md border-2 p-3 shadow-sm hover:shadow-md transition-shadow duration-200 ${scope.color}`}
            >
              {/* Scope Header */}
              <div className="flex items-center justify-between mb-2">
                <h3 className={`text-sm font-semibold ${scope.textColor}`}>
                  {scope.icon} {scope.name}
                </h3>
                <div className={`text-xs px-2 py-1 rounded-full ${
                  isIncrease 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {isIncrease ? '📈' : '📉'} {Math.abs(changePercent).toFixed(1)}%
                </div>
              </div>

              {/* Base Year Row */}
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Base Year</span>
                  <span className="text-xs text-gray-400">({data.baseYearValue})</span>
                </div>
                <div className="mt-1">
                  <span className="text-sm font-medium text-gray-700">
                    {scope.baseValue.toLocaleString('en-US', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">kg CO2e</span>
                </div>
              </div>

              {/* Reporting Year Row */}
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600 font-medium">Reporting Year</span>
                  <span className="text-xs text-gray-400">({data.selectedYearValue})</span>
                </div>
                <div className="mt-1">
                  <span className="text-sm font-bold text-gray-900">
                    {scope.reportingValue.toLocaleString('en-US', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">kg CO2e</span>
                </div>
              </div>

              {/* Change Indicator */}
              <div className="pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Change</span>
                  <span className={`text-xs font-medium ${
                    isIncrease ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {isIncrease ? '+' : ''}{change.toLocaleString('en-US', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })} kg CO2e
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Row */}
      <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-xs text-gray-500">Total Base Year</div>
            <div className="text-sm font-semibold text-gray-700">
              {data.baseYear.total.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })} kg CO2e
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">Total Reporting Year</div>
            <div className="text-sm font-semibold text-gray-900">
              {data.selectedYear.total.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })} kg CO2e
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">Total Change</div>
            <div className={`text-sm font-semibold ${
              (data.selectedYear.total - data.baseYear.total) >= 0 
                ? 'text-red-600' 
                : 'text-green-600'
            }`}>
              {(data.selectedYear.total - data.baseYear.total) >= 0 ? '+' : ''}
              {(data.selectedYear.total - data.baseYear.total).toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })} kg CO2e
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}








